/**
 * NOS-125: Media speakers CRUD with avatar upload.
 * NOS-262: config over the shared taxonomy scaffold.
 */
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  MediaImageUploadField,
  MediaTaxonomyPage,
  type TaxonomyColumn,
} from "@/components/dashboard/MediaTaxonomyPage"
import {
  useMediaSpeakers,
  type MediaSpeaker,
  type MediaSpeakerInput,
} from "@/hooks/useMediaSpeakers"

type SpeakerForm = {
  name: string
  title: string
  bio: string
  avatar_url: string | null
}

const columns: TaxonomyColumn<MediaSpeaker>[] = [
  {
    headClassName: "w-[56px]",
    cell: (s) =>
      s.avatar_url ? (
        <img src={s.avatar_url} alt="" className="h-10 w-10 rounded-full object-cover" />
      ) : (
        <div className="h-10 w-10 rounded-full bg-muted" />
      ),
  },
  { header: "Name", cell: (s) => s.name, cellClassName: "font-medium" },
  { header: "Title", cell: (s) => s.title ?? "—", cellClassName: "text-muted-foreground" },
]

export function MediaSpeakersPage() {
  return (
    <MediaTaxonomyPage<MediaSpeaker, SpeakerForm, MediaSpeakerInput>
      useTaxonomy={useMediaSpeakers}
      title="Speakers"
      tagline="People who appear in your media library."
      entity="Speaker"
      columns={columns}
      dialogClassName="max-w-lg"
      emptyForm={{ name: "", title: "", bio: "", avatar_url: null }}
      formFromRow={(s) => ({
        name: s.name,
        title: s.title ?? "",
        bio: s.bio ?? "",
        avatar_url: s.avatar_url,
      })}
      validate={(f) => (f.name.trim() ? null : "Name is required")}
      toPayload={(f) => ({
        name: f.name,
        title: f.title,
        bio: f.bio,
        avatar_url: f.avatar_url,
      })}
      deleteWarning="Remove this speaker from all videos before deleting."
      renderFields={(form, setForm) => (
        <>
          <div>
            <Label>Name</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
          </div>
          <div>
            <Label>Title / role</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="Senior Pastor"
            />
          </div>
          <div>
            <Label>Bio</Label>
            <Textarea
              value={form.bio}
              onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))}
              rows={4}
            />
          </div>
          <div>
            <Label>Avatar</Label>
            <MediaImageUploadField
              folder="speaker-avatars"
              successMessage="Avatar uploaded"
              onUploaded={(url) => setForm((f) => ({ ...f, avatar_url: url }))}
            />
          </div>
        </>
      )}
    />
  )
}
